/* eslint-disable no-nested-ternary */
import React from 'react';
import Typography from '@digital-hig/mui/dist/Typography';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import produce from 'immer';
// eslint-disable-next-line import/no-extraneous-dependencies
import Grid from '@mui/material/Grid';
import RadioButton from '@mui/material/Radio';
// eslint-disable-next-line import/no-extraneous-dependencies
import Checkbox from '@mui/material/Checkbox';
import DOMPurify from 'isomorphic-dompurify';
// eslint-disable-next-line import/no-extraneous-dependencies
import FormControlLabel from '@mui/material/FormControlLabel';
// eslint-disable-next-line import/no-extraneous-dependencies
import SvgIcon from '@mui/material/SvgIcon';
import SvgError from '@digital-hig/icon/lib/esm/display-icons/error';
import SvgComplete from '@digital-hig/icon/lib/esm/display-icons/complete';
import { styleVariables, QUIZ_TYPE_MULTI } from '../common/constants';

const QuizTypeSingleAndMulti = ({
  question, answers, setAnswers, isSignedIn, quizState, isEveryQuestionAnswered,
}) => {
  const handleRadioChange = (e) => {
    const updatedAnswers = produce(answers, (draftState) => {
      draftState[e.target.name] = [e.target.value];
    });

    setAnswers(updatedAnswers);
    isEveryQuestionAnswered(updatedAnswers, quizState);
  };

  const handleCheckBoxChange = (event) => {
    const updatedAnswers = produce(answers, (draftState) => {
      if (event.target.checked) { // User selected an answer
        if (!Array.isArray(draftState[event.target.name])) {
          draftState[event.target.name] = [event.target.value];
        } else {
          draftState[event.target.name].push(event.target.value);
        }
      } else { // User deselected an answer
        draftState[event.target.name] = draftState[event.target.name].filter((answer) => answer !== event.target.value);
        if (!draftState[event.target.name].length) {
          delete draftState[event.target.name];
        }
      }
    });
    setAnswers(updatedAnswers);
    isEveryQuestionAnswered(updatedAnswers, quizState);
  };

  const isCheckboxDisabled = ({ qid, aval }) => {
    if (question.type === QUIZ_TYPE_MULTI) {
      if (question.maximumAnswers) { // False if there is not a max answer value
        if (answers[qid] && !answers[qid].includes(aval)) { // False if the answer is selected
          if (answers[qid] && answers[qid].length >= question.maximumAnswers) { // True if not selected and max reached
            return true;
          }
        }
      }
      return false;
    }
    return false;
  };

  // TODO:: As this is common for all problems, it should to be moved to quiz.js and send as callback
  const displayErrorOrSuccess = (answer) => {
    if (answers[question.id] && answers[question.id][0] === answer.id && Object.hasOwnProperty.call(question, 'isCorrect')) {
      if (!question.isCorrect) {
        return (
          <Grid className="answer-error">
            <SvgIcon size="medium"><SvgError /></SvgIcon>
            <Typography component="span" variant="body-copy-small" color="ink-error" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(`Incorrect. ${question.explanation}`) }} />
          </Grid>
        );
      }
      return (
        <Grid className="answer-success">
          <SvgIcon size="medium"><SvgComplete /></SvgIcon>
          <Typography component="span" variant="body-copy-small">
            Nice work
          </Typography>
        </Grid>
      );
    }

    return <></>;
  };

  const isChecked = ({ qid, aval }) => ((answers[qid] && answers[qid].includes(aval)) || false);

  return (
    <RootElement>
      <Grid id={`qid${question.id}`} container flexDirection="column" key={question.id} className={`${quizState.showUnanswered ? 'showUnanswered' : ''}`}>
        <Grid item lg={6} md={9} className={`${quizState.unansweredQuestions.includes(question.id) ? 'unanswered' : 'answered'}`}>
          <Grid className="question-title">
            <Typography component="h4" variant="header-copy-small" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(question.text) }} />
            <Typography component="span" variant="body-copy-small" color="ink-on-background-60">
              {question.type === QUIZ_TYPE_MULTI ? `Select up to ${question.maximumAnswers}` : '(Select one)'}
            </Typography>
          </Grid>
          <Grid component="ul">
            {question.answers.map((answer) => (
              <Grid component="li" key={`${question.id}${answer.id}`}>
                <FormControlLabel
                  disabled={isCheckboxDisabled({ qid: question.id, aval: answer.id }) || !isSignedIn || quizState.showReset}
                  label={answer.text
                    ? <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(answer.text) }} /> : <></>
                  + answer.image ? <div><img src={answer.image.url} alt={answer.image.alt} /></div> : <></>}
                  checked={isChecked({ qid: question.id, aval: answer.id })}
                  className={(answers[question.id] && answers[question.id].includes(answer.id)) ? 'selected' : ''}
                  data-showreset={quizState.showReset}
                  // TODO:: declare separate method and pass question and aswer and return Control to avoid duplicate props declaration.
                  control={question.type === QUIZ_TYPE_MULTI
                    ? (
                      <Checkbox
                        id={`aid${question.id}-${answer.id}`}
                        value={answer.id}
                        name={question.id}
                        onChange={handleCheckBoxChange}
                      />
                    )
                    : (
                      <RadioButton
                        id={`aid${question.id}-${answer.id}`}
                        value={answer.id}
                        name={question.id}
                        onChange={handleRadioChange}
                      />
                    )}
                />
                {displayErrorOrSuccess(answer)}
              </Grid>
            ))}
          </Grid>
        </Grid>
        <Grid item className="error">
          <SvgIcon size="medium"><SvgError /></SvgIcon>
          <Typography component="span" className="error" variant="body-copy-small" color="ink-error">Select an answer</Typography>
        </Grid>
      </Grid>
    </RootElement>
  );
};

const RootElement = styled.div`
  margin-top: 30px;
  .error {
    padding-top: 0;
  }
  .answered + .error, .unanswered + .error{
    display: none;
  }
  .showUnanswered {
    .unanswered {
      border: 1px solid red;
      padding: 12px !important;
      border-radius: 6px;
    }
    .unanswered + .error {
      display: block;
      padding-top: 0;
    }
  }
  .answer-error, .answer-success{
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 18px;
    margin-top: -4px;
  }  
  .question-title {
    margin-bottom: 22px;
  }
   ul {
    list-style: none;
    margin: 0;
    padding: 0;

    li label {
      border: 1px solid rgba(204, 204, 204, 0.25);
      border-radius: 6px;
      margin-bottom: 10px;
      box-shadow: 0 1px 0px rgba(102, 102, 102, 0.8);
      display: flex;
      padding: 14px;
      margin-right: 0;
      user-select: none;
      &.selected {
        background-color: rgba(204, 204, 204, 0.25);
      }

      &[data-showreset="true"].selected {
        border: 2px solid ${styleVariables.inkOnBackground};
        background: none;
        box-shadow: none;
      }
      &[data-showreset="true"].MuiFormControlLabel-root.Mui-disabled{
        opacity: 1;
      }

      &:hover:not(.selected){
        background: radial-gradient(65.63% 63.74% at 91.94% 0%, rgba(128, 128, 255, 0.15) 0%, rgba(128, 128, 255, 0) 100%);
        border: 2px solid rgba(95, 96, 255, 1);
        box-shadow: none;
        padding: 13px;
      }
      
    }
   }
  
`;

QuizTypeSingleAndMulti.propTypes = {
  question: PropTypes.arrayOf({}).isRequired,
  answers: PropTypes.shape({}).isRequired,
  setAnswers: PropTypes.func.isRequired,
  isSignedIn: PropTypes.bool.isRequired,
  quizState: PropTypes.shape({
    showReset: PropTypes.bool,
    unansweredQuestions: PropTypes.arrayOf(),
    showUnanswered: PropTypes.string,
  }).isRequired,
  isEveryQuestionAnswered: PropTypes.func.isRequired,
};

export default React.memo(QuizTypeSingleAndMulti);
